import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { clearSelectedProduct, fetchProductByIdAsync,resetProductUpdateStatus, selectProductUpdateStatus, selectSelectedProduct, updateProductByIdAsync } from '../../products/ProductSlice'
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography, useMediaQuery, useTheme, FormControlLabel, Checkbox } from '@mui/material'
import { useForm } from "react-hook-form"
import { selectBrands } from '../../brands/BrandSlice'
import { selectCategories } from '../../categories/CategoriesSlice'
import { toast } from 'react-toastify'

export const ProductUpdate = () => {

    const {register,handleSubmit, reset} = useForm()

    const {id}=useParams()
    const dispatch=useDispatch()
    const selectedProduct=useSelector(selectSelectedProduct)
    const brands=useSelector(selectBrands)
    const categories=useSelector(selectCategories)
    const productUpdateStatus=useSelector(selectProductUpdateStatus)
    const navigate=useNavigate()
    const [thumbnailFile,setThumbnailFile]=useState(null)
    const [imageFiles,setImageFiles]=useState([])
    const [videoFile,setVideoFile]=useState(null)
    const [isRecommended, setIsRecommended] = useState(false)
    const [isTopSeller, setIsTopSeller] = useState(false)
    const theme=useTheme()
    const is1100=useMediaQuery(theme.breakpoints.down(1100))
    const is480=useMediaQuery(theme.breakpoints.down(480))


    useEffect(()=>{
        if(id){
            dispatch(fetchProductByIdAsync(id))
        }
    },[dispatch, id])

    useEffect(()=>{
        if(selectedProduct){
            // keep the same number of slots user already has (fallback to 4)
            const slots=Math.max(selectedProduct.images?.length || 0,4)
            setImageFiles(Array(slots).fill(null))
            setIsRecommended(selectedProduct.isRecommended || false)
            setIsTopSeller(selectedProduct.isTopSeller || false)
            // reset react-hook-form with current values
            reset({
                title:selectedProduct.title,
                brand:selectedProduct.brand._id,
                category:selectedProduct.category._id,
                description:selectedProduct.description,
                price:selectedProduct.price,
                discountPercentage:selectedProduct.discountPercentage,
                stockQuantity:selectedProduct.stockQuantity,
                deliveryCharge:selectedProduct.deliveryCharge,
                onlinePaymentDiscount:selectedProduct.onlinePaymentDiscount
            })
        }
    },[reset, selectedProduct])

    useEffect(()=>{
        if(productUpdateStatus==='fullfilled'){
            toast.success("Product Updated")
            navigate("/admin/dashboard")
        }
        else if(productUpdateStatus==='rejected'){
            toast.error("Error updating product, please try again later")
        }
    },[navigate, productUpdateStatus])

    useEffect(()=>{
        return ()=>{
            dispatch(clearSelectedProduct())
            dispatch(resetProductUpdateStatus())
        }
    },[dispatch])

    const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve(reader.result)
            reader.onerror = (error) => reject(error)
        })

    const mergedImages = useMemo(()=>{
        if(!selectedProduct) return []
        const existing = selectedProduct.images || []
        const slots = Math.max(existing.length, imageFiles.length || 4)
        const paddedExisting=[...existing]
        while(paddedExisting.length<slots){paddedExisting.push(null)}
        return paddedExisting
    },[imageFiles.length, selectedProduct])

    const handleProductUpdate=async(data)=>{
        if(!selectedProduct) return

        try{
            const thumbnailPayload = thumbnailFile
                ? await fileToBase64(thumbnailFile)
                : selectedProduct.thumbnail

            const imagesPayload = await Promise.all(
                mergedImages.map(async (img,idx)=>{
                    const file=imageFiles[idx]
                    if(file){
                        return await fileToBase64(file)
                    }
                    return img
                })
            )

            const videoPayload = videoFile
                ? await fileToBase64(videoFile)
                : selectedProduct.video

            const filteredImages=imagesPayload.filter(Boolean)
            if(filteredImages.length===0){
                toast.error("At least one product image is required")
                return
            }

            const productUpdate={
                ...data,
                _id:selectedProduct._id,
                thumbnail:thumbnailPayload,
                images:filteredImages,
                video:videoPayload,
                isRecommended,
                isTopSeller
            }

            dispatch(updateProductByIdAsync(productUpdate))
        }catch(err){
            console.error(err)
            toast.error("Failed to read selected images")
        }
    }


  return (
    <Stack p={'0 16px'} justifyContent={'center'} alignItems={'center'} flexDirection={'row'} >
        
        {
            selectedProduct &&
        
        <Stack width={is1100?"100%":"60rem"} rowGap={4} mt={is480?4:6} mb={6} component={'form'} noValidate onSubmit={handleSubmit(handleProductUpdate)}> 
            
            {/* feild area */}
            <Stack rowGap={3}>
                <Stack>
                    <Typography variant='h6' fontWeight={400} gutterBottom>Title</Typography>
                    <TextField {...register("title",{required:'Title is required'})}/>
                </Stack> 

                <Stack flexDirection={'row'} >

                    <FormControl fullWidth>
                        <InputLabel id="brand-selection">Brand</InputLabel>
                        <Select defaultValue={selectedProduct.brand._id} {...register("brand",{required:"Brand is required"})} labelId="brand-selection" label="Brand">
                            
                            {
                                brands.map((brand)=>(
                                    <MenuItem value={brand._id}>{brand.name}</MenuItem>
                                ))
                            }

                        </Select>
                    </FormControl>


                    <FormControl fullWidth>
                        <InputLabel id="category-selection">Category</InputLabel>
                        <Select defaultValue={selectedProduct.category._id} {...register("category",{required:"category is required"})} labelId="category-selection" label="Category">
                            
                            {
                                categories.map((category)=>(
                                    <MenuItem value={category._id}>{category.name}</MenuItem>
                                ))
                            }

                        </Select>
                    </FormControl>

                </Stack>


                <Stack>
                    <Typography variant='h6' fontWeight={400}  gutterBottom>Description</Typography>
                    <TextField multiline rows={4} {...register("description",{required:"Description is required"})}/>
                </Stack>

                <Stack flexDirection={'row'}>
                    <Stack flex={1}>
                        <Typography variant='h6' fontWeight={400}  gutterBottom>Price</Typography>
                        <TextField type='number' {...register("price",{required:"Price is required"})}/>
                    </Stack>
                    <Stack flex={1}>
                        <Typography variant='h6' fontWeight={400}  gutterBottom>Discount {is480?"%":"Percentage"}</Typography>
                        <TextField type='number' {...register("discountPercentage",{required:"discount percentage is required"})}/>
                    </Stack>
                </Stack>

                <Stack flexDirection={'row'}>
                    <Stack flex={1}>
                        <Typography variant='h6' fontWeight={400}  gutterBottom>Delivery Charge</Typography>
                        <TextField type='number' {...register("deliveryCharge",{required:"Delivery charge is required"})}/>
                    </Stack>
                    <Stack flex={1}>
                        <Typography variant='h6' fontWeight={400}  gutterBottom>Online Pay Discount %</Typography>
                        <TextField type='number' {...register("onlinePaymentDiscount",{required:"Online payment discount is required"})}/>
                    </Stack>
                </Stack>

                <Stack>
                    <Typography variant='h6'  fontWeight={400} gutterBottom>Stock Quantity</Typography>
                    <TextField type='number' {...register("stockQuantity",{required:"Stock Quantity is required"})}/>
                </Stack>
                <Stack>
                    <Typography variant='h6'  fontWeight={400} gutterBottom>Thumbnail</Typography>
                    <Stack rowGap={1}>
                        <Button variant="outlined" component="label">
                            Replace thumbnail
                            <input hidden type="file" accept="image/*" onChange={(e)=>setThumbnailFile(e.target.files[0]||null)} />
                        </Button>
                        <Typography variant='body2' color='text.secondary'>
                            {thumbnailFile?.name ? `Selected: ${thumbnailFile.name}` : "Keeps current thumbnail unless a new file is chosen"}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>Current thumbnail is preserved if you don’t pick a file.</Typography>
                    </Stack>
                </Stack>

                <Stack>
                    <Typography variant='h6'  fontWeight={400} gutterBottom>Product Images</Typography>

                    <Stack rowGap={2}>
                        {mergedImages.map((image,idx)=>(
                            <Stack key={idx} rowGap={0.5}>
                                <Button variant="outlined" component="label">
                                    {image ? "Replace Image" : "Add Image"} {idx+1}
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*"
                                        onChange={(e)=>{
                                            const files=[...imageFiles]
                                            files[idx]=e.target.files[0]||null
                                            setImageFiles(files)
                                        }}
                                    />
                                </Button>
                                <Typography variant='body2' color='text.secondary'>
                                    {imageFiles[idx]?.name
                                        ? `Selected: ${imageFiles[idx].name}`
                                        : image
                                            ? "Existing image will be kept"
                                            : "No image set yet"}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>

                </Stack>

                <Stack>
                    <Typography variant='h6'  fontWeight={400} gutterBottom>Product Video (optional)</Typography>
                    <Button variant="outlined" component="label">
                        Replace / Add Video
                        <input hidden type="file" accept="video/*" onChange={(e)=>setVideoFile(e.target.files[0]||null)} />
                    </Button>
                    <Typography variant='body2' color='text.secondary'>
                        {videoFile?.name ? `Selected: ${videoFile.name}` : (selectedProduct.video ? "Existing video will be kept" : "No video set")}
                    </Typography>
                </Stack>

                <Stack>
                    <Typography variant='h6'  fontWeight={400} gutterBottom>Product Features</Typography>
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={isRecommended} 
                                onChange={(e) => setIsRecommended(e.target.checked)}
                            />
                        }
                        label="⭐ Mark as Recommended"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={isTopSeller} 
                                onChange={(e) => setIsTopSeller(e.target.checked)}
                            />
                        }
                        label="🔥 Mark as Top Seller"
                    />
                </Stack>

            </Stack>


            {/* action area */}
            <Stack flexDirection={'row'} alignSelf={'flex-end'} columnGap={is480?1:2}>
                <Button size={is480?'medium':'large'} variant='contained' type='submit'>Update</Button>
                <Button size={is480?'medium':'large'} variant='outlined' color='error' component={Link} to={'/admin/dashboard'}>Cancel</Button>
            </Stack>


        </Stack>
        }

    </Stack>
  )
}
